import { Button, Tooltip } from '@heroui/react';
import { BringToFront, Copy, Lock, SendToBack, Trash2, Unlock } from 'lucide-react';
import React, { FC } from 'react';

import { useAdsGeneratorStore, useSelectedElement, useSelectedElements } from '../../model/store';
import { PanelSection } from '../common';
import { EmptyPanelHint } from '../Editor/Editor.styled';
import { CommonProperties } from './CommonProperties';
import { ImageProperties } from './ImageProperties';
import { SelectionProperties } from './SelectionProperties';
import { ShapeProperties } from './ShapeProperties';
import { TextProperties } from './TextProperties';

export const Inspector: FC = () => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const element = useSelectedElement();
  const selection = useSelectedElements();
  const duplicateElement = useAdsGeneratorStore((state) => state.duplicateElement);
  const removeElement = useAdsGeneratorStore((state) => state.removeElement);
  const moveLayer = useAdsGeneratorStore((state) => state.moveLayer);
  const updateElement = useAdsGeneratorStore((state) => state.updateElement);

  if (!scene) return null;

  if (selection.length > 1) return <SelectionProperties elements={selection} />;

  if (!element) {
    return (
      <PanelSection title="Właściwości">
        <EmptyPanelHint>
          Kliknij element na planszy, żeby zmienić jego wygląd. Podwójne kliknięcie tekstu otwiera edycję treści.
        </EmptyPanelHint>
      </PanelSection>
    );
  }

  return (
    <>
      <PanelSection title={element.name}>
        <div className="flex flex-wrap gap-1">
          <Tooltip content="Duplikuj" size="sm">
            <Button size="sm" radius="sm" isIconOnly variant="flat" aria-label="Duplikuj" onPress={() => duplicateElement(element.id)}>
              <Copy size={16} />
            </Button>
          </Tooltip>
          <Tooltip content="Na wierzch" size="sm">
            <Button size="sm" radius="sm" isIconOnly variant="flat" aria-label="Na wierzch" onPress={() => moveLayer(element.id, 'front')}>
              <BringToFront size={16} />
            </Button>
          </Tooltip>
          <Tooltip content="Na spód" size="sm">
            <Button size="sm" radius="sm" isIconOnly variant="flat" aria-label="Na spód" onPress={() => moveLayer(element.id, 'back')}>
              <SendToBack size={16} />
            </Button>
          </Tooltip>
          <Tooltip content={element.locked ? 'Odblokuj' : 'Zablokuj'} size="sm">
            <Button
              size="sm"
              radius="sm"
              isIconOnly
              variant="flat"
              aria-label="Zablokuj"
              onPress={() => updateElement(element.id, { locked: !element.locked })}
            >
              {element.locked ? <Lock size={16} /> : <Unlock size={16} />}
            </Button>
          </Tooltip>
          <Tooltip content="Usuń" size="sm">
            <Button
              size="sm"
              radius="sm"
              isIconOnly
              variant="flat"
              color="danger"
              aria-label="Usuń"
              onPress={() => removeElement(element.id)}
            >
              <Trash2 size={16} />
            </Button>
          </Tooltip>
        </div>
      </PanelSection>

      {element.type === 'text' && <TextProperties element={element} />}
      {element.type === 'shape' && <ShapeProperties element={element} />}
      {element.type === 'image' && <ImageProperties element={element} />}

      <CommonProperties element={element} scene={scene} />
    </>
  );
};
